import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Typography,
  Space,
  Spin,
  Divider,
  message,
  Alert,
  Checkbox,
  Row,
  Col,
  Statistic,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  RobotOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

interface Dataset {
  id: number;
  name: string;
  buckets: string[];
  type: string;
}

interface Prompt {
  id: number;
  name: string;
  version_count: number;
}

interface Model {
  id: string;
  name: string;
  provider: string;
}

interface SizeStats {
  [key: string]: number;
}

interface AspectRatioStats {
  [key: string]: number;
}

const SIZE_CATEGORIES = [
  { value: 'extra_tiny', label: 'Extra Tiny', description: '< 100K px' },
  { value: 'tiny', label: 'Tiny', description: '100K-250K px' },
  { value: 'small', label: 'Small', description: '250K-500K px' },
  { value: 'medium', label: 'Medium', description: '500K-1M px' },
  { value: 'large', label: 'Large', description: '1M-4M px' },
  { value: 'extra_large', label: 'Extra Large', description: '> 4M px' },
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const CaptioningTaskCreate: React.FC = () => {
  const [form] = Form.useForm();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(null);
  const [sizeStats, setSizeStats] = useState<SizeStats>({});
  const [aspectRatioStats, setAspectRatioStats] = useState<AspectRatioStats>({});
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedAspectRatios, setSelectedAspectRatios] = useState<string[]>([]);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDatasetId) {
      fetchDatasetStats(selectedDatasetId);
    }
  }, [selectedDatasetId]);

  useEffect(() => {
    if (selectedDatasetId) {
      fetchEstimatedCount();
    }
  }, [selectedDatasetId, selectedSizes, selectedAspectRatios]);

  const fetchData = async () => {
    try {
      const [datasetsRes, promptsRes, modelsRes] = await Promise.all([
        axios.get(`${API_URL}/datasets`),
        axios.get(`${API_URL}/prompts`),
        axios.get(`${API_URL}/models`),
      ]);

      setDatasets(datasetsRes.data);
      setPrompts(promptsRes.data);
      setModels(modelsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDatasetStats = async (datasetId: number) => {
    setLoadingStats(true);
    try {
      const [sizeRes, aspectRes] = await Promise.all([
        axios.get(`${API_URL}/datasets/${datasetId}/size-stats`),
        axios.get(`${API_URL}/datasets/${datasetId}/aspect-ratio-stats`),
      ]);
      setSizeStats(sizeRes.data);
      setAspectRatioStats(aspectRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchEstimatedCount = async () => {
    if (!selectedDatasetId) return;

    try {
      const params: any = { page: 1, limit: 1 };
      if (selectedSizes.length > 0) {
        params.size = selectedSizes.join(',');
      }
      if (selectedAspectRatios.length > 0) {
        params.aspectRatio = selectedAspectRatios.join(',');
      }

      const response = await axios.get(`${API_URL}/datasets/${selectedDatasetId}/images`, { params });
      setEstimatedCount(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch count:', error);
    }
  };

  const handleDatasetChange = (datasetId: number) => {
    setSelectedDatasetId(datasetId);
    setSelectedSizes([]);
    setSelectedAspectRatios([]);
    setSizeStats({});
    setAspectRatioStats({});
    setEstimatedCount(null);
  };

  const handlePromptChange = (promptId: number) => {
    const prompt = prompts.find(p => p.id === promptId);
    setSelectedPrompt(prompt || null);
    // Set default version to latest
    if (prompt) {
      form.setFieldsValue({ prompt_version: prompt.version_count });
    }
  };

  const handleModelChange = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    if (model) {
      form.setFieldsValue({ provider: model.provider });
    }
  };

  const handleSizeChange = (size: string, checked: boolean) => {
    if (checked) {
      setSelectedSizes([...selectedSizes, size]);
    } else {
      setSelectedSizes(selectedSizes.filter(s => s !== size));
    }
  };

  const handleAspectRatioChange = (ratio: string, checked: boolean) => {
    if (checked) {
      setSelectedAspectRatios([...selectedAspectRatios, ratio]);
    } else {
      setSelectedAspectRatios(selectedAspectRatios.filter(r => r !== ratio));
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/captioning-tasks`, {
        name: values.name.trim(),
        dataset_id: values.dataset_id,
        prompt_id: values.prompt_id,
        prompt_version: values.prompt_version,
        model: values.model,
        provider: values.provider,
        batch_size: values.batch_size,
        requests_per_second: values.requests_per_second,
        size_filter: selectedSizes.length > 0 ? selectedSizes : null,
        aspect_ratio_filter: selectedAspectRatios.length > 0 ? selectedAspectRatios : null,
      });

      message.success('Captioning task created successfully');
      navigate(`/captioning/${response.data.id}`);
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to create captioning task');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/captioning')}
          style={{ marginBottom: '16px' }}
        >
          Back to Tasks
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          <RobotOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          Create Captioning Task
        </Title>
      </div>

      <Card style={{ maxWidth: '700px' }}>
        {datasets.length === 0 && (
          <Alert
            message="No Datasets Available"
            description="Please create a dataset first before creating a captioning task."
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {prompts.length === 0 && (
          <Alert
            message="No Prompts Available"
            description="Please create a prompt first before creating a captioning task."
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
          initialValues={{
            batch_size: 100,
            requests_per_second: 6,
          }}
        >
          <Form.Item
            name="name"
            label="Task Name"
            rules={[{ required: true, message: 'Please enter a task name' }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>

          <Form.Item
            name="dataset_id"
            label="Dataset"
            rules={[{ required: true, message: 'Please select a dataset' }]}
          >
            <Select
              placeholder="Select a dataset"
              disabled={datasets.length === 0}
              onChange={handleDatasetChange}
            >
              {datasets.map(dataset => (
                <Option key={dataset.id} value={dataset.id}>
                  {dataset.name} ({dataset.buckets.length} buckets)
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedDatasetId && (
            <>
              <Divider>Image Filters</Divider>

              {loadingStats ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Size Filter</Text>
                    <Text type="secondary" style={{ marginLeft: '8px' }}>(select none for all)</Text>
                    <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
                      {SIZE_CATEGORIES.map(cat => {
                        const count = sizeStats[cat.value] || 0;
                        return (
                          <Col key={cat.value} span={8}>
                            <Checkbox
                              checked={selectedSizes.includes(cat.value)}
                              onChange={(e) => handleSizeChange(cat.value, e.target.checked)}
                            >
                              {cat.label} ({count.toLocaleString()})
                            </Checkbox>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong>Aspect Ratio Filter</Text>
                    <Text type="secondary" style={{ marginLeft: '8px' }}>(select none for all)</Text>
                    <Row gutter={[8, 8]} style={{ marginTop: '8px' }}>
                      {Object.entries(aspectRatioStats)
                        .sort((a, b) => b[1] - a[1])
                        .map(([ratio, count]) => (
                          <Col key={ratio} span={8}>
                            <Checkbox
                              checked={selectedAspectRatios.includes(ratio)}
                              onChange={(e) => handleAspectRatioChange(ratio, e.target.checked)}
                            >
                              {ratio} ({count.toLocaleString()})
                            </Checkbox>
                          </Col>
                        ))}
                    </Row>
                  </div>

                  {estimatedCount !== null && (
                    <Card size="small" style={{ marginBottom: '16px', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                      <Statistic
                        title="Estimated Images to Caption"
                        value={estimatedCount}
                        prefix={<PictureOutlined />}
                        valueStyle={{ color: '#52c41a' }}
                      />
                      {(selectedSizes.length > 0 || selectedAspectRatios.length > 0) && (
                        <div style={{ marginTop: '8px' }}>
                          <Text type="secondary">Filters applied: </Text>
                          {selectedSizes.map(s => (
                            <Tag key={s} color="blue">{s}</Tag>
                          ))}
                          {selectedAspectRatios.map(r => (
                            <Tag key={r} color="purple">{r}</Tag>
                          ))}
                        </div>
                      )}
                    </Card>
                  )}
                </>
              )}
            </>
          )}

          <Form.Item
            name="model"
            label="Model"
            rules={[{ required: true, message: 'Please select a model' }]}
          >
            <Select placeholder="Select a model" onChange={handleModelChange}>
              {models.map(model => (
                <Option key={model.id} value={model.id}>
                  {model.name} ({model.provider})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="provider" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="prompt_id"
            label="Prompt"
            rules={[{ required: true, message: 'Please select a prompt' }]}
          >
            <Select
              placeholder="Select a prompt"
              disabled={prompts.length === 0}
              onChange={handlePromptChange}
            >
              {prompts.map(prompt => (
                <Option key={prompt.id} value={prompt.id}>
                  {prompt.name} (v{prompt.version_count})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="prompt_version"
            label="Prompt Version"
            rules={[{ required: true, message: 'Please select a version' }]}
          >
            <InputNumber
              min={1}
              max={selectedPrompt?.version_count || 1}
              style={{ width: '100%' }}
              placeholder="Select version"
            />
          </Form.Item>

          <Divider>Processing Settings</Divider>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item
              name="batch_size"
              label="Batch Size"
              tooltip="Number of images to fetch per batch from the database"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={1000} style={{ width: '150px' }} />
            </Form.Item>

            <Form.Item
              name="requests_per_second"
              label="Requests/Second"
              tooltip="API requests per second per worker. Grok limit is 8 req/s (480/min)."
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={20} style={{ width: '150px' }} />
            </Form.Item>
          </Space>

          <Alert
            message="Note"
            description="Make sure you have active API keys configured for the selected model's provider before starting the task."
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button onClick={() => navigate('/captioning')}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                icon={<PlusOutlined />}
                disabled={datasets.length === 0 || prompts.length === 0}
              >
                Create Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CaptioningTaskCreate;
